// Directions panel — origin/destination input, vehicle selector,
// route alternatives and turn-by-turn steps.

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavStore } from '../../stores/navStore';
import { useMapStore } from '../../stores';
import {
  TRAVEL_MODES,
  getRoute,
  formatDuration,
  formatRouteDistance,
  bearingToCompass,
  type TravelMode,
} from '../../lib/routing';
import {
  getCurrentPosition,
  forwardGeocode,
  GeoError,
} from '../../lib/geolocation';
import { cn, calculateDistance, formatDistance } from '../../lib/utils';
import { fitBounds, bboxOf } from '../../lib/mapBridge';
import {
  estimateCongestion,
  congestionTone,
  congestionDot,
  corridorKey,
  getTrips,
  arrivalTime,
  compareDepartures,
  formatClock,
  formatDay,
} from '../../lib/traffic';
import type { Coordinates } from '../../types';

// Padding leaves room for the docked panel (right), controls (left) and the
// top/bottom chrome so the whole route stays visible.
const FRAME_PADDING = {
  top: 90,
  bottom: 90,
  left: 300,
  right: 420,
};
import {
  CarIcon,
  MotorbikeIcon,
  BikeIcon,
  WalkIcon,
  OriginMarkerIcon,
  DestinationMarkerIcon,
  maneuverIcon,
} from './NavIcons';
import {
  X,
  Search,
  Crosshair,
  ArrowLeftRight,
  Loader2,
  Navigation,
  ChevronDown,
  ChevronUp,
  MapPin,
  Route as RouteIcon,
  AlertTriangle,
  Footprints,
  CircleCheck,
  Info,
  Maximize,
  Clock3,
} from 'lucide-react';

const MODE_ICONS: Record<TravelMode, React.ComponentType<{ className?: string }>> = {
  car: CarIcon,
  motorbike: MotorbikeIcon,
  bike: BikeIcon,
  foot: WalkIcon,
};

export function DirectionsPanel() {
  const {
    origin,
    destination,
    setOrigin,
    setDestination,
    swapEndpoints,
    useMyLocationAsOrigin,
    mode,
    setMode,
    route,
    setRoute,
    activeRouteIndex,
    setActiveRouteIndex,
    routing,
    setRouting,
    routeError,
    setRouteError,
    showSteps,
    setShowSteps,
    closePanel,
    whereAmI,
  } = useNavStore();

  const { setMapState, addLeads } = useMapStore();

  const [originQuery, setOriginQuery] = useState(origin.label);
  const [destQuery, setDestQuery] = useState(destination.label);
  const [locatingOrigin, setLocatingOrigin] = useState(false);

  const abortRef = useRef<AbortController | null>(null);

  // Keep inputs in sync with the store
  useEffect(() => setOriginQuery(origin.label), [origin.label]);
  useEffect(() => setDestQuery(destination.label), [destination.label]);

  // --- geocode helper ---
  const resolvePlace = useCallback(
    async (text: string): Promise<{ coords: Coordinates; label: string } | null> => {
      const results = await forwardGeocode(text);
      return results[0] ?? null;
    },
    []
  );

  // --- perform routing ---
  const runRoute = useCallback(
    async (from = origin, to = destination, travelMode = mode) => {
      if (!from.coords || !to.coords) return;

      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;

      setRouting(true);
      setRouteError(null);

      try {
        const result = await getRoute({
          origin: from.coords,
          destination: to.coords,
          mode: travelMode,
          alternatives: true,
          signal: ctrl.signal,
        });
        setRoute(result);

        // Frame the whole route in the *visible* part of the map, leaving
        // room for the docked navigation panel on the right.
        const coords = result.routes[0]?.coordinates;
        if (coords?.length) {
          const box = bboxOf(coords);
          const framed = box ? fitBounds(box, { padding: FRAME_PADDING, maxZoom: 15 }) : false;
          if (!framed) {
            // Map not ready — approximate with a centre/zoom
            if (box) {
              setMapState({
                center: { lat: (box[0][1] + box[1][1]) / 2, lng: (box[0][0] + box[1][0]) / 2 },
                zoom: 13,
                pitch: 0,
              });
            }
          }
        }
      } catch (err) {
        if ((err as Error)?.name === 'AbortError') return;
        setRouteError(
          err instanceof Error ? err.message : 'Could not calculate a route.'
        );
      } finally {
        setRouting(false);
      }
    },
    [origin, destination, mode, setRouting, setRouteError, setRoute, setMapState]
  );

  // Re-route automatically when endpoints or mode change
  useEffect(() => {
    if (origin.coords && destination.coords) {
      runRoute();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origin.coords?.lat, origin.coords?.lng, destination.coords?.lat, destination.coords?.lng, mode]);

  // --- handlers ---
  const handleSubmitOrigin = async () => {
    if (!originQuery.trim()) return;
    const hit = await resolvePlace(originQuery.trim());
    if (hit) setOrigin(hit);
  };

  const handleSubmitDestination = async () => {
    if (!destQuery.trim()) return;
    const hit = await resolvePlace(destQuery.trim());
    if (hit) setDestination(hit);
  };

  const handleUseMyLocation = async () => {
    setLocatingOrigin(true);
    try {
      const pos = await getCurrentPosition();
      setOrigin({ coords: pos.coords, label: 'My location' });
      useMyLocationAsOrigin();
      setOrigin({ coords: pos.coords, label: 'My location' });
    } catch (err) {
      setRouteError(
        err instanceof GeoError ? err.message : 'Could not get your location.'
      );
    } finally {
      setLocatingOrigin(false);
    }
  };

  const handleSwap = () => {
    swapEndpoints();
    setOriginQuery(destination.label);
    setDestQuery(origin.label);
  };

  const activeRoute = route?.routes[Math.min(activeRouteIndex, route.routes.length - 1)] ?? null;

  const straightDistance =
    origin.coords && destination.coords
      ? calculateDistance(origin.coords, destination.coords)
      : null;

  // ---- Traffic + ETA ----
  const freeFlowMinutes = activeRoute ? activeRoute.duration / 60 : 0;
  const corridor = origin.coords && destination.coords
    ? corridorKey(
        `${origin.coords.lat.toFixed(3)},${origin.coords.lng.toFixed(3)}`,
        `${destination.coords.lat.toFixed(3)},${destination.coords.lng.toFixed(3)}`
      )
    : '';

  const congestion = useMemo(
    () => estimateCongestion(freeFlowMinutes, corridor ? getTrips(corridor) : []),
    [freeFlowMinutes, corridor]
  );

  // Traffic-adjusted ETA
  const travelMinutes = Math.round(freeFlowMinutes * congestion.factor);
  const departAt = new Date();
  const arriveAt = arrivalTime(departAt, travelMinutes);
  const departures = useMemo(
    () => compareDepartures(freeFlowMinutes, congestion),
    [freeFlowMinutes, congestion.factor, congestion.level]
  );
  const fastestDeparture = departures.reduce(
    (best, d) => (d.durationMinutes < best.durationMinutes ? d : best),
    departures[0]
  );

  return (
    <div className="flex h-full flex-col bg-white">
      {/* ---------- Header ---------- */}
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
        <div className="flex items-center gap-2">
          <RouteIcon className="h-4 w-4 text-primary-600" />
          <h2 className="text-sm font-semibold text-slate-900">Directions</h2>
        </div>
        <button
          onClick={closePanel}
          className="rounded-lg p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900"
          aria-label="Close directions"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* ---------- Inputs ---------- */}
      <div className="space-y-2 border-b border-slate-200 p-4">
        {/* Origin */}
        <div className="group relative flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500/20">
          <OriginMarkerIcon className="h-4 w-4 shrink-0 text-slate-500" />
          <input
            value={originQuery}
            onChange={(e) => setOriginQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSubmitOrigin()}
            onBlur={handleSubmitOrigin}
            placeholder="Choose starting point"
            className="w-full bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400"
          />
          <button
            onClick={handleUseMyLocation}
            disabled={locatingOrigin}
            title="Use my current location"
            className="shrink-0 rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-primary-600 disabled:opacity-50"
          >
            {locatingOrigin ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Crosshair className="h-4 w-4" />
            )}
          </button>
        </div>

        {/* Swap */}
        <div className="relative flex justify-center">
          <button
            onClick={handleSwap}
            title="Swap origin and destination"
            className="-my-1.5 rounded-full border border-slate-200 bg-white p-1 text-slate-500 shadow-sm transition-colors hover:border-slate-300 hover:text-slate-900"
          >
            <ArrowLeftRight className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* Destination */}
        <div className="group relative flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500/20">
          <DestinationMarkerIcon className="h-4 w-4 shrink-0 text-primary-600" />
          <input
            value={destQuery}
            onChange={(e) => setDestQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSubmitDestination()}
            onBlur={handleSubmitDestination}
            placeholder="Choose destination"
            className="w-full bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400"
          />
        </div>

        {/* Mode selector */}
        <div className="grid grid-cols-4 gap-1.5 pt-1">
          {TRAVEL_MODES.map((m) => {
            const Icon = MODE_ICONS[m.id];
            const isActive = mode === m.id;
            return (
              <button
                key={m.id}
                onClick={() => setMode(m.id)}
                title={m.description}
                className={cn(
                  'flex flex-col items-center gap-1 rounded-lg border py-2 transition-all duration-150',
                  isActive
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:bg-slate-50'
                )}
              >
                <Icon className="h-5 w-5" />
                <span className="text-[10px] font-medium leading-none">{m.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ---------- Results ---------- */}
      <div className="flex-1 overflow-y-auto">
        {/* Errors */}
        {routeError && (
          <div className="m-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <p className="text-xs text-amber-800">{routeError}</p>
          </div>
        )}

        {/* Loading */}
        {routing && (
          <div className="flex items-center gap-3 p-4">
            <Loader2 className="h-5 w-5 animate-spin text-primary-600" />
            <span className="text-sm text-slate-600">Calculating route…</span>
          </div>
        )}

        {/* Empty state */}
        {!routing && !route && !routeError && (
          <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
            <div className="mb-3 rounded-full bg-slate-100 p-3">
              <Navigation className="h-6 w-6 text-slate-400" />
            </div>
            <p className="text-sm font-medium text-slate-700">No route yet</p>
            <p className="mt-1 text-xs text-slate-500">
              Enter a start and destination, then choose a travel mode.
            </p>
          </div>
        )}

        {/* Route summary + alternatives */}
        {route && !routing && (
          <>
            {/* Primary summary */}
            <div className="border-b border-slate-100 p-4">
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-slate-900">
                  {formatDuration(activeRoute?.duration ?? 0)}
                </span>
                <span className="text-sm text-slate-500">
                  ({formatRouteDistance(activeRoute?.distance ?? 0)})
                </span>
              </div>

              {/* Arrival time */}
              {activeRoute && (
                <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-600">
                  <Clock3 className="h-3.5 w-3.5 text-slate-400" />
                  <span>
                    Leave now → arrive{' '}
                    <span className="font-semibold text-slate-900">
                      {formatDay(arriveAt)} {formatClock(arriveAt)}
                    </span>
                  </span>
                  {congestion.factor > 1.05 && (
                    <span className="text-slate-500">
                      (with traffic:{' '}
                      <span className="font-semibold text-slate-900">
                        {formatClock(arrivalTime(departAt, travelMinutes))}
                      </span>
                      )
                    </span>
                  )}
                </div>
              )}

              {/* Traffic estimate */}
              <div
                className={cn(
                  'mt-3 flex items-start gap-2 rounded-lg border p-2.5',
                  congestionTone(congestion.level)
                )}
              >
                <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-current" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold">{congestion.label}</p>
                  <p className="mt-0.5 text-[11px] opacity-80">
                    {congestion.level === 'unknown' ? (
                      <>
                        Turn on Live tracking while driving and this corridor
                        learns your real travel times.
                      </>
                    ) : (
                      <>
                        {Math.round(congestion.factor * 100)}% of free-flow time
                        {congestion.observedSpeed != null &&
                          ` · you average ${congestion.observedSpeed} km/h`}
                        {` · ${congestion.observations} observations`}
                      </>
                    )}
                  </p>
                  <p className="mt-1 text-[10px] opacity-70">
                    Estimated from your own GPS readings — no external traffic
                    service.
                  </p>
                </div>
              </div>

              {/* distance bar */}
              <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full bg-primary-500 transition-all duration-500"
                  style={{
                    width: `${Math.min(
                      100,
                      Math.max(
                        8,
                        (straightDistance && activeRoute
                          ? (activeRoute.distance / straightDistance) * 22
                          : 25)
                      )
                    )}%`,
                  }}
                />
              </div>

              {straightDistance != null && activeRoute && (
                <p className="mt-2 text-xs text-slate-500">
                  Direct distance {formatDistance(straightDistance)} · route is{' '}
                  {Math.round((activeRoute.distance / straightDistance) * 100)}% of direct
                </p>
              )}

              {/* Departure comparison */}
              {activeRoute && congestion.level !== 'unknown' && (
                <div className="mt-3">
                  <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                    Leave at
                  </p>
                  <div className="grid grid-cols-4 gap-1.5">
                    {departures.map((d) => {
                      const isFastest = d.offsetMinutes === fastestDeparture.offsetMinutes;
                      const isNow = d.offsetMinutes === 0;
                      return (
                        <div
                          key={d.offsetMinutes}
                          className={cn(
                            'rounded-lg border px-1.5 py-1.5 text-center',
                            isFastest
                              ? 'border-primary-400 bg-primary-50'
                              : 'border-slate-200 bg-white'
                          )}
                          title={`Depart ${formatClock(d.departAt)}, arrive ${formatClock(d.arriveAt)}`}
                        >
                          <p className="text-[10px] font-medium text-slate-500">
                            {isNow ? 'Now' : `+${d.offsetMinutes}m`}
                          </p>
                          <p className="mt-0.5 text-xs font-semibold text-slate-900">
                            {formatClock(d.arriveAt)}
                          </p>
                          <p className="text-[9px] text-slate-400">
                            {d.durationMinutes} min
                          </p>
                        </div>
                      );
                    })}
                  </div>
                  {fastestDeparture.offsetMinutes > 0 && (
                    <p className="mt-1.5 text-[11px] text-slate-500">
                      Leaving in {fastestDeparture.offsetMinutes} min is the fastest
                      option right now.
                    </p>
                  )}
                </div>
              )}

              {route.estimated && (
                <div className="mt-3 flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 p-2">
                  <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-600" />
                  <p className="text-[11px] text-blue-800">
                    Live routing server unreachable — showing a straight-line estimate.
                  </p>
                </div>
              )}

              {route.provider !== 'offline-estimate' && (
                <div className="mt-2 flex items-center justify-between gap-2">
                  <p className="text-[10px] text-slate-400">
                    Routing by {route.provider}
                  </p>
                  <button
                    onClick={() => {
                      const coords = activeRoute?.coordinates;
                      if (!coords?.length) return;
                      const box = bboxOf(coords);
                      if (box) fitBounds(box, { padding: FRAME_PADDING, maxZoom: 15 });
                    }}
                    className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[10px] font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900"
                  >
                    <Maximize className="h-3 w-3" />
                    Fit route
                  </button>
                </div>
              )}
            </div>

            {/* Alternatives */}
            {route.routes.length > 1 && (
              <div className="border-b border-slate-100">
                <p className="px-4 pb-2 pt-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  {route.routes.length} routes
                </p>
                <div className="space-y-1 px-2 pb-2">
                  {route.routes.map((r, i) => (
                    <button
                      key={r.id}
                      onClick={() => setActiveRouteIndex(i)}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left transition-colors',
                        i === activeRouteIndex
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-transparent hover:bg-slate-50'
                      )}
                    >
                      <span
                        className={cn(
                          'shrink-0 text-lg font-semibold',
                          i === activeRouteIndex ? 'text-primary-700' : 'text-slate-400'
                        )}
                      >
                        {i === 0 ? 'Best' : `Alt ${i}`}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-slate-800">
                          {formatDuration(r.duration)}
                        </span>
                        <span className="block text-xs text-slate-500">
                          {formatRouteDistance(r.distance)}
                        </span>
                      </span>
                      {i === activeRouteIndex && (
                        <CircleCheck className="h-4 w-4 shrink-0 text-primary-600" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Turn-by-turn steps */}
            {activeRoute && activeRoute.steps.length > 0 && (
              <div>
                <button
                  onClick={() => setShowSteps(!showSteps)}
                  className="flex w-full items-center gap-2 px-4 py-3 text-left transition-colors hover:bg-slate-50"
                >
                  <Footprints className="h-4 w-4 shrink-0 text-slate-500" />
                  <span className="flex-1 text-sm font-medium text-slate-700">
                    {activeRoute.steps.length} steps
                  </span>
                  {showSteps ? (
                    <ChevronUp className="h-4 w-4 shrink-0 text-slate-400" />
                  ) : (
                    <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
                  )}
                </button>

                {showSteps && (
                  <div className="border-t border-slate-100">
                    {activeRoute.steps.map((step, i) => {
                      const Icon = maneuverIcon(step.maneuverType, step.modifier ?? '');
                      const isLast = i === activeRoute.steps.length - 1;
                      return (
                        <div
                          key={i}
                          className={cn(
                            'flex gap-3 px-4 py-2.5 transition-colors hover:bg-slate-50',
                            !isLast && 'border-b border-slate-50'
                          )}
                        >
                          <div className="flex w-5 shrink-0 justify-center pt-0.5">
                            <Icon className="h-4 w-4 text-slate-600" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm text-slate-800">{step.instruction}</p>
                            <p className="mt-0.5 text-xs text-slate-500">
                              {formatRouteDistance(step.distance)} ·{' '}
                              {formatDuration(step.duration)}
                              {step.name ? ` · ${step.name}` : ''}
                            </p>
                          </div>
                          {step.bearingAfter > 0 && (
                            <span className="shrink-0 self-center text-[10px] font-medium text-slate-400">
                              {bearingToCompass(step.bearingAfter)}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* No steps (estimate fallback) */}
            {activeRoute && activeRoute.steps.length === 0 && (
              <div className="border-t border-slate-100 p-4 text-center">
                <p className="text-xs text-slate-500">
                  Turn-by-turn steps are unavailable for estimated routes.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default DirectionsPanel;